import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  User, MapPin, FileText, Wallet, ShieldAlert, GraduationCap, Briefcase,
  Loader2, CheckCircle2, AlertTriangle, Save, Send, Upload, Eye,
} from "lucide-react";
import { toast } from "sonner";

export const ONBOARDING_SECTIONS = [
  { key: "personal", label: "Personal", icon: User },
  { key: "address", label: "Address", icon: MapPin },
  { key: "id_proof", label: "ID proofs", icon: FileText },
  { key: "bank", label: "Bank & Tax", icon: Wallet },
  { key: "emergency", label: "Emergency contact", icon: ShieldAlert },
  { key: "education", label: "Education", icon: GraduationCap },
  { key: "experience", label: "Work experience", icon: Briefcase },
  { key: "review", label: "Review & Submit", icon: Send },
];
const SECTIONS = ONBOARDING_SECTIONS;

export const ONBOARDING_EMPTY = {
  personal: { dob: "", gender: "", marital_status: "", blood_group: "", father_name: "", mother_name: "" },
  address: { current_line1: "", current_city: "", current_state: "", current_pincode: "", permanent_same: true, permanent_line1: "", permanent_city: "", permanent_state: "", permanent_pincode: "" },
  id_proof: { aadhaar: "", pan: "", passport: "", voter_id: "", aadhaar_file_id: "", aadhaar_file_name: "", pan_file_id: "", pan_file_name: "" },
  bank: { account_holder: "", account_number: "", ifsc: "", bank_name: "", branch: "", uan: "", pf_number: "", bank_statement_file_id: "", bank_statement_file_name: "", cheque_file_id: "", cheque_file_name: "", passbook_file_id: "", passbook_file_name: "" },
  emergency: { name: "", relation: "", phone: "", alt_phone: "", address: "" },
  education: { highest_qualification: "", university: "", year_of_passing: "", percentage: "", marksheet_10_file_id: "", marksheet_10_file_name: "", marksheet_12_file_id: "", marksheet_12_file_name: "", graduation_file_id: "", graduation_file_name: "" },
  experience: { years_total: "", previous_company: "", previous_designation: "", previous_ctc: "", payslip1_file_id: "", payslip1_file_name: "", payslip2_file_id: "", payslip2_file_name: "", payslip3_file_id: "", payslip3_file_name: "", exp_bank_statement_file_id: "", exp_bank_statement_file_name: "", relieving_file_id: "", relieving_file_name: "", offer_letter_file_id: "", offer_letter_file_name: "" },
};
const EMPTY = ONBOARDING_EMPTY;

export default function MyOnboarding() {
  const [rec, setRec] = useState(null);
  const [details, setDetails] = useState(EMPTY);
  const [active, setActive] = useState("personal");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [minSubmit, setMinSubmit] = useState(90);

  useEffect(() => {
    api.get("/my-onboarding")
      .then((r) => {
        setRec(r.data);
        setDetails({ ...EMPTY, ...(r.data?.details || {}) });
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"));
    api.get("/onboarding-settings")
      .then((r) => setMinSubmit(r.data?.min_submit_progress ?? 90))
      .catch(() => setMinSubmit(90));
  }, []);

  const update = (section, patch) => setDetails((d) => ({ ...d, [section]: { ...d[section], ...patch } }));

  // Auto-jump-to-Review hint: when progress crosses the submission threshold
  // for the first time AND the user isn't already on the Review step, show
  // a one-time toast with a "Go to Review" action.
  const crossedRef = useRef(false);
  useEffect(() => {
    const p = rec?.progress || 0;
    const status_ = rec?.submission_status || "draft";
    if (
      status_ === "draft"
      && p >= minSubmit
      && !crossedRef.current
      && active !== "review"
    ) {
      crossedRef.current = true;
      toast.success(`You're ready to submit (${p}%)`, {
        description: "Open the Review & Submit step to finalize.",
        action: { label: "Go to Review", onClick: () => setActive("review") },
        duration: 8000,
      });
    }
    if (p < minSubmit) crossedRef.current = false;
  }, [rec?.progress, rec?.submission_status, minSubmit, active]);

  const save = async (submit = false) => {
    const setter = submit ? setSubmitting : setSaving;
    setter(true);
    try {
      const { data } = await api.post("/my-onboarding/save", { details, submit });
      setRec(data);
      toast.success(submit ? "Submitted for approval" : "Saved as draft");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setter(false);
    }
  };

  if (!rec) {
    return <div className="text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  const status = rec.submission_status || "draft";
  const isApproved = status === "approved";
  const isSubmitted = status === "submitted";
  const isRejected = status === "rejected";

  return (
    <div data-testid="my-onboarding-page">
      <PageHeader
        title="My Onboarding"
        subtitle="Fill in your details and submit them to HR/Manager for approval."
      />

      <StatusBanner status={status} rec={rec} />

      <div className="grid grid-cols-12 gap-6 mt-4">
        {/* Sidebar */}
        <Card className="hr-card p-2 col-span-12 lg:col-span-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 px-3 py-2">Sections</div>
          {SECTIONS.map((s) => {
            const isReview = s.key === "review";
            const filled = !isReview && !!details[s.key] && Object.values(details[s.key] || {}).some((v) => v !== "" && v !== null && v !== undefined);
            return (
              <button
                key={s.key}
                data-testid={`section-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 text-sm ${
                  active === s.key ? "bg-[#F26B3A]/10 text-[#0F172A] font-medium" : "text-gray-700 hover:bg-gray-50"
                } ${isReview ? "border-t border-gray-100 mt-1 pt-3" : ""}`}
              >
                <s.icon className="h-4 w-4" />
                <span className="flex-1">{s.label}</span>
                {filled && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                {isReview && (rec.progress || 0) >= minSubmit && status === "draft" && (
                  <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Ready</span>
                )}
              </button>
            );
          })}
          <div className="mt-3 px-3 pb-2">
            <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Progress</div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#1E88E5]" style={{ width: `${rec.progress || 0}%` }} />
            </div>
            <div className="text-[11px] text-gray-500 mt-1 font-mono">{rec.progress || 0}%</div>
          </div>
        </Card>

        {/* Form */}
        <Card className="hr-card p-6 col-span-12 lg:col-span-9">
          <SectionForm
            section={active}
            data={details[active] || {}}
            onChange={(patch) => update(active, patch)}
            readOnly={isApproved || isSubmitted}
            allData={details}
            updateRoot={(section, patch) => update(section, patch)}
            setActive={setActive}
          />

          <div className="flex flex-wrap items-center gap-2 mt-6 pt-4 border-t border-gray-100">
            {!isApproved && !isSubmitted && (
              <>
                <Button
                  data-testid="save-draft"
                  variant="outline"
                  onClick={() => save(false)}
                  disabled={saving || submitting}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save draft
                </Button>

                {/* Step navigation — Next on non-review steps, Submit only on review step */}
                {active !== "review" ? (
                  <Button
                    data-testid="step-next"
                    variant="outline"
                    onClick={() => {
                      const idx = SECTIONS.findIndex((s) => s.key === active);
                      if (idx >= 0 && idx < SECTIONS.length - 1) setActive(SECTIONS[idx + 1].key);
                    }}
                    className="ml-auto"
                  >
                    Next: {SECTIONS[(SECTIONS.findIndex((s) => s.key === active) + 1) % SECTIONS.length]?.label} →
                  </Button>
                ) : (
                  <>
                    <Button
                      data-testid="submit-for-approval"
                      onClick={() => save(true)}
                      disabled={saving || submitting || (rec.progress || 0) < minSubmit}
                      title={(rec.progress || 0) < minSubmit ? `Complete at least ${minSubmit}% to submit (currently ${rec.progress || 0}%)` : ""}
                      className="ml-auto bg-[#F26B3A] hover:bg-[#d85d2f] disabled:opacity-60"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                      {isRejected ? "Resubmit for approval" : "Submit for approval"}
                    </Button>
                    {(rec.progress || 0) < minSubmit && (
                      <span className="text-[11px] text-amber-600 ml-1 w-full mt-1" data-testid="submit-progress-hint">
                        Reach at least {minSubmit}% to enable submission ({rec.progress || 0}% / {minSubmit}%)
                      </span>
                    )}
                  </>
                )}
              </>
            )}
            {isSubmitted && (
              <div className="text-xs text-amber-700">Waiting for HR/Manager approval — details are locked.</div>
            )}
            {isApproved && (
              <div className="text-xs text-emerald-700">Approved. Contact HR for any changes.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatusBanner({ status, rec }) {
  if (status === "approved") {
    return (
      <Card className="p-4 bg-emerald-50 border-emerald-200 flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-emerald-800">Onboarding approved</div>
          <div className="text-emerald-700 text-xs">Approved by {rec.approved_by} on {(rec.approved_at || "").slice(0,10)}</div>
        </div>
      </Card>
    );
  }
  if (status === "submitted") {
    return (
      <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-3">
        <Loader2 className="h-5 w-5 text-amber-600 mt-0.5 animate-spin" />
        <div className="text-sm">
          <div className="font-medium text-amber-800">Pending approval</div>
          <div className="text-amber-700 text-xs">Submitted on {(rec.submitted_at || "").slice(0,10)} — your details are locked while HR reviews.</div>
        </div>
      </Card>
    );
  }
  if (status === "rejected") {
    return (
      <Card className="p-4 bg-rose-50 border-rose-200 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-rose-800">Correction requested</div>
          <div className="text-rose-700 text-xs">{rec.rejection_reason || "Please update your details and resubmit."}</div>
        </div>
      </Card>
    );
  }
  return null;
}

function FieldRow({ label, children, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gray-500">{label}</Label>
      <div className="mt-1.5" data-testid={testId}>{children}</div>
    </div>
  );
}

export function SectionForm({ section, data, onChange, readOnly, allData, updateRoot, setActive }) {
  const ro = { disabled: readOnly };
  switch (section) {
    case "personal":
      return (
        <Grid title="Personal details">
          <FieldRow label="Date of birth" testId="fr-dob"><Input type="date" {...ro} value={data.dob || ""} onChange={(e) => onChange({ dob: e.target.value })} /></FieldRow>
          <FieldRow label="Gender" testId="fr-gender"><Input {...ro} value={data.gender || ""} onChange={(e) => onChange({ gender: e.target.value })} placeholder="Male / Female / Other" /></FieldRow>
          <FieldRow label="Marital status" testId="fr-marital"><Input {...ro} value={data.marital_status || ""} onChange={(e) => onChange({ marital_status: e.target.value })} /></FieldRow>
          <FieldRow label="Blood group" testId="fr-blood"><Input {...ro} value={data.blood_group || ""} onChange={(e) => onChange({ blood_group: e.target.value })} /></FieldRow>
          <FieldRow label="Father's name" testId="fr-father"><Input {...ro} value={data.father_name || ""} onChange={(e) => onChange({ father_name: e.target.value })} /></FieldRow>
          <FieldRow label="Mother's name" testId="fr-mother"><Input {...ro} value={data.mother_name || ""} onChange={(e) => onChange({ mother_name: e.target.value })} /></FieldRow>
        </Grid>
      );
    case "address":
      return (
        <div className="space-y-6">
          <Grid title="Current address">
            <FieldRow label="Address line" testId="fr-curr-line"><Input {...ro} value={data.current_line1 || ""} onChange={(e) => onChange({ current_line1: e.target.value })} /></FieldRow>
            <FieldRow label="City" testId="fr-curr-city"><Input {...ro} value={data.current_city || ""} onChange={(e) => onChange({ current_city: e.target.value })} /></FieldRow>
            <FieldRow label="State" testId="fr-curr-state"><Input {...ro} value={data.current_state || ""} onChange={(e) => onChange({ current_state: e.target.value })} /></FieldRow>
            <FieldRow label="PIN code" testId="fr-curr-pin"><Input {...ro} value={data.current_pincode || ""} onChange={(e) => onChange({ current_pincode: e.target.value })} /></FieldRow>
          </Grid>
          <Grid title="Permanent address">
            <FieldRow label="Address line" testId="fr-perm-line"><Input {...ro} value={data.permanent_line1 || ""} onChange={(e) => onChange({ permanent_line1: e.target.value })} /></FieldRow>
            <FieldRow label="City" testId="fr-perm-city"><Input {...ro} value={data.permanent_city || ""} onChange={(e) => onChange({ permanent_city: e.target.value })} /></FieldRow>
            <FieldRow label="State" testId="fr-perm-state"><Input {...ro} value={data.permanent_state || ""} onChange={(e) => onChange({ permanent_state: e.target.value })} /></FieldRow>
            <FieldRow label="PIN code" testId="fr-perm-pin"><Input {...ro} value={data.permanent_pincode || ""} onChange={(e) => onChange({ permanent_pincode: e.target.value })} /></FieldRow>
          </Grid>
        </div>
      );
    case "id_proof":
      return (
        <div className="space-y-6">
          <Grid title="Identity documents">
            <FieldRow label="Aadhaar number" testId="fr-aadhaar"><Input {...ro} value={data.aadhaar || ""} onChange={(e) => onChange({ aadhaar: e.target.value })} placeholder="12-digit number" /></FieldRow>
            <FieldRow label="PAN number" testId="fr-pan"><Input {...ro} value={data.pan || ""} onChange={(e) => onChange({ pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" /></FieldRow>
            <FieldRow label="Passport" testId="fr-passport"><Input {...ro} value={data.passport || ""} onChange={(e) => onChange({ passport: e.target.value })} /></FieldRow>
            <FieldRow label="Voter ID" testId="fr-voter"><Input {...ro} value={data.voter_id || ""} onChange={(e) => onChange({ voter_id: e.target.value })} /></FieldRow>
          </Grid>

          <div>
            <div className="font-display text-base font-semibold text-[#0F172A] mb-1">Upload documents</div>
            <p className="text-xs text-gray-500 mb-3">Upload clear scans/photos of your Aadhaar Card and PAN Card (JPG, PNG, or PDF · max 10&nbsp;MB each)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DocUploader
                label="Aadhaar Card"
                testId="aadhaar-upload"
                fileId={data.aadhaar_file_id}
                fileName={data.aadhaar_file_name}
                readOnly={readOnly}
                onUploaded={(fid, name) => onChange({ aadhaar_file_id: fid, aadhaar_file_name: name })}
                onRemove={() => onChange({ aadhaar_file_id: "", aadhaar_file_name: "" })}
                category="aadhaar"
              />
              <DocUploader
                label="PAN Card"
                testId="pan-upload"
                fileId={data.pan_file_id}
                fileName={data.pan_file_name}
                readOnly={readOnly}
                onUploaded={(fid, name) => onChange({ pan_file_id: fid, pan_file_name: name })}
                onRemove={() => onChange({ pan_file_id: "", pan_file_name: "" })}
                category="pan"
              />
            </div>
          </div>
        </div>
      );
    case "bank":
      return (
        <div className="space-y-6">
          <Grid title="Bank & statutory details">
            <FieldRow label="Account holder name" testId="fr-acct-name"><Input {...ro} value={data.account_holder || ""} onChange={(e) => onChange({ account_holder: e.target.value })} /></FieldRow>
            <FieldRow label="Account number" testId="fr-acct-no"><Input {...ro} value={data.account_number || ""} onChange={(e) => onChange({ account_number: e.target.value })} /></FieldRow>
            <FieldRow label="IFSC" testId="fr-ifsc"><Input {...ro} value={data.ifsc || ""} onChange={(e) => onChange({ ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" /></FieldRow>
            <FieldRow label="Bank name" testId="fr-bank"><Input {...ro} value={data.bank_name || ""} onChange={(e) => onChange({ bank_name: e.target.value })} /></FieldRow>
            <FieldRow label="Branch" testId="fr-branch"><Input {...ro} value={data.branch || ""} onChange={(e) => onChange({ branch: e.target.value })} /></FieldRow>
            <FieldRow label="UAN number" testId="fr-uan"><Input {...ro} value={data.uan || ""} onChange={(e) => onChange({ uan: e.target.value })} /></FieldRow>
            <FieldRow label="PF number" testId="fr-pf"><Input {...ro} value={data.pf_number || ""} onChange={(e) => onChange({ pf_number: e.target.value })} /></FieldRow>
          </Grid>
          <UploadGroup
            title="Upload bank documents"
            help="Any one of these helps HR verify your account. Upload all if available (JPG, PNG, or PDF · max 10 MB each)."
            items={[
              { label: "Bank Statement", category: "bank_statement", idKey: "bank_statement_file_id", nameKey: "bank_statement_file_name", testId: "bank-statement-upload" },
              { label: "Cancelled Cheque", category: "cheque", idKey: "cheque_file_id", nameKey: "cheque_file_name", testId: "cheque-upload" },
              { label: "Passbook", category: "passbook", idKey: "passbook_file_id", nameKey: "passbook_file_name", testId: "passbook-upload" },
            ]}
            data={data}
            onChange={onChange}
            readOnly={readOnly}
          />
        </div>
      );
    case "emergency":
      return (
        <Grid title="Emergency contact">
          <FieldRow label="Contact name" testId="fr-em-name"><Input {...ro} value={data.name || ""} onChange={(e) => onChange({ name: e.target.value })} /></FieldRow>
          <FieldRow label="Relation" testId="fr-em-rel"><Input {...ro} value={data.relation || ""} onChange={(e) => onChange({ relation: e.target.value })} placeholder="Spouse / Parent / Sibling" /></FieldRow>
          <FieldRow label="Phone" testId="fr-em-phone"><Input {...ro} value={data.phone || ""} onChange={(e) => onChange({ phone: e.target.value })} /></FieldRow>
          <FieldRow label="Alternate phone" testId="fr-em-alt"><Input {...ro} value={data.alt_phone || ""} onChange={(e) => onChange({ alt_phone: e.target.value })} /></FieldRow>
          <div className="md:col-span-2">
            <FieldRow label="Address" testId="fr-em-addr"><Textarea {...ro} rows={2} value={data.address || ""} onChange={(e) => onChange({ address: e.target.value })} /></FieldRow>
          </div>
        </Grid>
      );
    case "education":
      return (
        <div className="space-y-6">
          <Grid title="Education">
            <FieldRow label="Highest qualification" testId="fr-qual"><Input {...ro} value={data.highest_qualification || ""} onChange={(e) => onChange({ highest_qualification: e.target.value })} placeholder="B.Tech / BSc / Diploma" /></FieldRow>
            <FieldRow label="University / Board" testId="fr-uni"><Input {...ro} value={data.university || ""} onChange={(e) => onChange({ university: e.target.value })} /></FieldRow>
            <FieldRow label="Year of passing" testId="fr-yop"><Input {...ro} value={data.year_of_passing || ""} onChange={(e) => onChange({ year_of_passing: e.target.value })} /></FieldRow>
            <FieldRow label="Percentage / CGPA" testId="fr-pct"><Input {...ro} value={data.percentage || ""} onChange={(e) => onChange({ percentage: e.target.value })} /></FieldRow>
          </Grid>
          <UploadGroup
            title="Upload education certificates"
            help="Upload your 10th, Intermediate (12th) and Graduation certificates / marksheets."
            items={[
              { label: "10th Marksheet", category: "edu_10th", idKey: "marksheet_10_file_id", nameKey: "marksheet_10_file_name", testId: "edu-10-upload" },
              { label: "Intermediate / 12th Certificate", category: "edu_12th", idKey: "marksheet_12_file_id", nameKey: "marksheet_12_file_name", testId: "edu-12-upload" },
              { label: "Graduation Certificate", category: "edu_grad", idKey: "graduation_file_id", nameKey: "graduation_file_name", testId: "edu-grad-upload" },
            ]}
            data={data}
            onChange={onChange}
            readOnly={readOnly}
          />
        </div>
      );
    case "experience":
      return (
        <div className="space-y-6">
          <Grid title="Previous experience">
            <FieldRow label="Total experience (years)" testId="fr-yrs"><Input {...ro} value={data.years_total || ""} onChange={(e) => onChange({ years_total: e.target.value })} /></FieldRow>
            <FieldRow label="Previous company" testId="fr-prev-co"><Input {...ro} value={data.previous_company || ""} onChange={(e) => onChange({ previous_company: e.target.value })} /></FieldRow>
            <FieldRow label="Previous designation" testId="fr-prev-desig"><Input {...ro} value={data.previous_designation || ""} onChange={(e) => onChange({ previous_designation: e.target.value })} /></FieldRow>
            <FieldRow label="Previous CTC (INR)" testId="fr-prev-ctc"><Input {...ro} value={data.previous_ctc || ""} onChange={(e) => onChange({ previous_ctc: e.target.value })} /></FieldRow>
          </Grid>
          <UploadGroup
            title="Upload previous-employment documents"
            help="Upload your last 3 months' payslips, bank statement, offer letter and relieving letter from your previous employer."
            items={[
              { label: "Payslip — Month 1", category: "exp_payslip_1", idKey: "payslip1_file_id", nameKey: "payslip1_file_name", testId: "exp-payslip1" },
              { label: "Payslip — Month 2", category: "exp_payslip_2", idKey: "payslip2_file_id", nameKey: "payslip2_file_name", testId: "exp-payslip2" },
              { label: "Payslip — Month 3", category: "exp_payslip_3", idKey: "payslip3_file_id", nameKey: "payslip3_file_name", testId: "exp-payslip3" },
              { label: "Bank Statement (3 mo)", category: "exp_bank_statement", idKey: "exp_bank_statement_file_id", nameKey: "exp_bank_statement_file_name", testId: "exp-bank" },
              { label: "Offer Letter", category: "exp_offer", idKey: "offer_letter_file_id", nameKey: "offer_letter_file_name", testId: "exp-offer" },
              { label: "Relieving Letter", category: "exp_relieving", idKey: "relieving_file_id", nameKey: "relieving_file_name", testId: "exp-relieving" },
            ]}
            data={data}
            onChange={onChange}
            readOnly={readOnly}
          />
        </div>
      );
    case "review":
      return <ReviewSummary allData={allData} setActive={setActive} />;
    default:
      return null;
  }
}

function ReviewSummary({ allData, setActive }) {
  // Count filled fields per section (ignore empty strings / null / undefined).
  const sectionStats = SECTIONS.filter((s) => s.key !== "review").map((s) => {
    const obj = allData[s.key] || {};
    const total = Object.keys(obj).length;
    const filled = Object.values(obj).filter((v) => v !== "" && v !== null && v !== undefined && v !== false).length;
    const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
    return { ...s, filled, total, pct };
  });

  return (
    <div className="space-y-5" data-testid="review-section">
      <div>
        <div className="font-display text-xl font-semibold text-[#0F172A] mb-1">Review &amp; Submit</div>
        <p className="text-sm text-gray-600">
          Review the information you have entered in each section. Once you are confident
          everything is complete and accurate, click <span className="font-semibold">Submit for approval</span> below.
          Your details will be locked and sent to HR / Manager for review.
        </p>
      </div>

      <Card className="hr-card p-4 divide-y divide-gray-100" data-testid="review-checklist">
        {sectionStats.map((s) => {
          const isComplete = s.pct >= 80;
          return (
            <div key={s.key} className="py-3 flex items-center gap-3">
              <div className={`h-9 w-9 rounded-lg grid place-items-center ${
                isComplete ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
              }`}>
                {isComplete ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F172A]">{s.label}</div>
                <div className="text-[11px] text-gray-500 font-mono">
                  {s.filled} / {s.total} fields filled · {s.pct}%
                </div>
              </div>
              <button
                type="button"
                data-testid={`edit-${s.key}`}
                onClick={() => setActive(s.key)}
                className="text-xs text-[#1E88E5] hover:underline"
              >
                Edit →
              </button>
            </div>
          );
        })}
      </Card>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800" data-testid="review-disclaimer">
        <div className="font-medium mb-0.5">Before you submit</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Make sure your <span className="font-medium">Aadhaar, PAN, bank account</span> and <span className="font-medium">emergency contact</span> are accurate.</li>
          <li>Document scans must be readable (JPG / PNG / PDF, max 10&nbsp;MB each).</li>
          <li>Once submitted, the form will be locked. HR can request changes if anything is missing.</li>
        </ul>
      </div>
    </div>
  );
}

function Grid({ title, children }) {
  return (
    <div>
      <div className="font-display text-lg font-semibold text-[#0F172A] mb-3">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function UploadGroup({ title, help, items, data, onChange, readOnly }) {
  return (
    <div>
      <div className="font-display text-base font-semibold text-[#0F172A] mb-1">{title}</div>
      {help && <p className="text-xs text-gray-500 mb-3">{help}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <DocUploader
            key={it.idKey}
            label={it.label}
            testId={it.testId}
            fileId={data[it.idKey]}
            fileName={data[it.nameKey]}
            readOnly={readOnly}
            category={it.category}
            onUploaded={(fid, name) => onChange({ [it.idKey]: fid, [it.nameKey]: name })}
            onRemove={() => onChange({ [it.idKey]: "", [it.nameKey]: "" })}
          />
        ))}
      </div>
    </div>
  );
}

function DocUploader({ label, testId, fileId, fileName, readOnly, onUploaded, onRemove, category }) {
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/files/upload?category=${category}`, fd);
      onUploaded(data.id, data.original_filename);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const view = () => {
    if (!fileId) return;
    const token = localStorage.getItem("hr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download?auth=${token}`, "_blank");
  };

  return (
    <div className="border border-gray-200 rounded-md p-4" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">{label}</div>
      {fileId ? (
        <div className="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-emerald-600" />
          <span className="flex-1 truncate text-gray-800">{fileName || "Uploaded"}</span>
          <button
            type="button"
            onClick={view}
            data-testid={`${testId}-view`}
            className="text-[#1E88E5] hover:underline text-xs flex items-center gap-1"
          >
            <Eye className="h-3.5 w-3.5" /> View
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={onRemove}
              data-testid={`${testId}-remove`}
              className="text-rose-600 hover:underline text-xs"
            >
              Remove
            </button>
          )}
        </div>
      ) : readOnly ? (
        <div className="text-xs text-gray-400 italic">Not uploaded</div>
      ) : (
        <label className="block cursor-pointer">
          <input
            type="file"
            accept="image/*,.pdf,application/pdf"
            onChange={onPick}
            className="hidden"
            data-testid={`${testId}-input`}
          />
          <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center hover:border-[#F26B3A] transition-colors">
            {uploading ? (
              <div className="text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" />Uploading…</div>
            ) : (
              <div className="text-xs text-gray-500">
                <Upload className="h-4 w-4 mx-auto mb-1 text-gray-400" />
                Click to upload {label}
              </div>
            )}
          </div>
        </label>
      )}
    </div>
  );
}
